import { style, styleVariants } from '@vanilla-extract/css';
import { vars } from '@/shared/ui/theme/contract.css';
import { mobile } from '@/shared/ui/theme/media';

/**
 * 「먼저 볼 것」 (퀘스트 7ca8908c) — 승인 시안 CandidatesAfter.
 *
 * 가로 558(IA §3-2) 안에서 선다: 점 8 · 제목 가변 · 수 · 칩. 칩은 글자 폭만큼만 쓴다.
 *
 * 줄은 한 겹 30px 이다(퀘스트 2f546c56). 사유별 건수는 제목 옆에 붙고, 설명은 펼친 본문으로 갔다 —
 * 두 겹 48px 일 때 다섯 묶음이 후보 선택 화면 높이의 38% 를 썼다. 폰은 탭 영역 44px 를 지킨다.
 */
export const list = style({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${vars.surface.border.subtle}`,
  borderRadius: vars.spacing.borderRadius.md,
  overflow: 'hidden',
});

export const group = style({
  borderBottom: `1px solid ${vars.surface.border.subtle}`,
});

const rowGrid = {
  display: 'grid',
  gridTemplateColumns: '8px minmax(0, 1fr) auto auto',
  gap: vars.spacing.gap.sm,
  alignItems: 'center',
  padding: `${vars.spacing.padding.sm} ${vars.spacing.padding.md}`,
} as const;

export const head = style({
  ...rowGrid,
  width: '100%',
  minHeight: '30px',
  paddingBlock: vars.spacing.padding.xs,
  background: 'transparent',
  border: 0,
  color: vars.typography.textColor.primary,
  textAlign: 'left',
  font: 'inherit',
  cursor: 'pointer',
  '@media': { [mobile]: { minHeight: '44px' } },
  selectors: {
    '&:hover': { background: vars.surface.card.hover },
    '&:focus-visible': {
      outline: `${vars.focus.ring.width} ${vars.focus.ring.style} ${vars.focus.ring.color}`,
      outlineOffset: `calc(-1 * ${vars.focus.ring.width})`,
    },
  },
});

export const cleanRow = style({
  ...rowGrid,
  minHeight: '30px',
  paddingBlock: vars.spacing.padding.xs,
  color: vars.typography.textColor.secondary,
});

const dotBase = style({
  width: '8px',
  height: '8px',
  borderRadius: vars.spacing.borderRadius.full,
});

export const dot = styleVariants({
  DO_NOT_SEND: [dotBase, { background: vars.bright.status.error }],
  OTHER_TEAM_RECENT: [dotBase, { background: vars.bright.status.warning }],
  ALREADY_ANSWERED: [dotBase, { background: vars.typography.textColor.tertiary }],
  SHARED_ADDRESS: [dotBase, { background: vars.bright.status.info }],
  NO_ADDRESS: [dotBase, { background: vars.typography.textColor.tertiary }],
});

export const dotClean = style([dotBase, { background: vars.bright.status.success }]);

export const titleBox = style({
  display: 'flex',
  alignItems: 'baseline',
  gap: vars.spacing.gap.sm,
  minWidth: 0,
});

export const title = style({
  fontSize: vars.typography.fontSize.sm,
  fontWeight: String(vars.typography.fontWeight.medium),
  whiteSpace: 'nowrap',
});

export const counts = style({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.secondary,
});

export const count = style({
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  fontWeight: String(vars.typography.fontWeight.bold),
  color: vars.typography.textColor.primary,
});

export const chip = style({
  justifySelf: 'end',
  display: 'inline-flex',
  alignItems: 'center',
  gap: vars.spacing.gap.xs,
  whiteSpace: 'nowrap',
});

export const body = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.sm,
  padding: `0 ${vars.spacing.padding.md} ${vars.spacing.padding.sm} 30px`,
});

export const rows = style({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.xs,
});

export const row = style({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr) auto',
  gap: vars.spacing.gap.sm,
  alignItems: 'center',
  fontSize: vars.typography.fontSize.xs,
});

export const handle = style({
  color: vars.typography.textColor.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const detail = style({
  color: vars.typography.textColor.secondary,
});

export const note = style({
  margin: 0,
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.tertiary,
});

export const actions = style({
  display: 'flex',
  flexWrap: 'wrap',
  gap: vars.spacing.gap.sm,
});

export const all = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.xs,
});

/**
 * 묶음 명단의 자기 칸 — 한 페이지가 200행이라 그대로 펼치면 아래 회신 큐가 수천 px 밀린다.
 * 후보 표의 자기 칸 스크롤(#2970)과 같은 방식이다. 행 열 줄 남짓만 보인다.
 */
export const scroll = style({
  position: 'relative',
  maxHeight: '220px',
  overflowY: 'auto',
});
